import clsx from 'clsx';
import { Grid, Theme, Box, Button as MuiButton, ButtonProps, styled } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { NaosButton } from '@dtsl/react';
import LocalVideoPreview from './LocalVideoPreview/LocalVideoPreview';
import SettingsMenu from './SettingsMenu/SettingsMenu';
import { Steps } from '../PreJoinScreens';
import ToggleAudioButton from '../../Buttons/ToggleAudioButton/ToggleAudioButton';
import ToggleVideoButton from '../../Buttons/ToggleVideoButton/ToggleVideoButton';
import { useAppState } from '../../../state';
import useVideoContext from '../../../hooks/useVideoContext/useVideoContext';
import CopyLink from './CopyLink/CopyLink';
import { useTranslation } from 'react-i18next';

const StyledMobileButton = styled(MuiButton)<ButtonProps>(() => ({
  padding: '0.8em 0',
  margin: 0,
}));

const StyledDeviceButton = styled(MuiButton)<ButtonProps>(() => ({
  width: '100%',
  margin: '1em 0',
  '& .MuiButton-startIcon': {
    margin: 0,
  },
}));

const useStyles = makeStyles((theme: Theme) => ({
  header: {
    marginBottom: '1em',
  },
  marginTop: {
    marginTop: '1em',
  },
  localPreviewContainer: {
    [theme.breakpoints.down('sm')]: {
      padding: '0 2.5em',
    },
  },
  joinButtons: {
    display: 'flex',
    justifyContent: 'end',
    margin: '2em 0 0',
    width: '100%',
    '& button': {
      margin: '0 .25em',
    },
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'end',
      justifyContent: 'center',
      margin: '1em 0 0',
      '& button': {
        margin: '0.25em 0',
      },
    },
  },
  mobileButtonBar: {
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      justifyContent: 'space-between',
      margin: '1.5em 0 1em',
    },
  },
}));

interface DeviceSelectionScreenProps {
  name: string;
  slug: string;
  room: string;
  setStep: (step: Steps) => void;
}

export default function DeviceSelectionScreen({ name, slug, room, setStep }: DeviceSelectionScreenProps) {
  const classes = useStyles();
  const { getToken, isFetching, user } = useAppState();
  const { connect, isAcquiringLocalTracks, isConnecting } = useVideoContext();
  const disableButtons = isFetching || isAcquiringLocalTracks || isConnecting;
  const { t } = useTranslation();
  const componentKey = isAcquiringLocalTracks ? 'acquiring' : 'ready';

  const handleJoin = () => {
    getToken(name, slug, room)
      .then((session) => connect(session))
      .catch(() => {
        // Error is already handled by AppStateProvider and displayed in ErrorDialog
      });
  };

  return (
    <>
      <div className={clsx(classes.header, 'sib-typo_heading-md')}>
        {t('PREVIEW.DEVICES.TITLE_1')}
        {name}
        {t('PREVIEW.DEVICES.TITLE_2')}
      </div>
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <CopyLink />
      </Box>
      <Grid container justifyContent="center">
        <Grid item md={2} sm={12} xs={12}></Grid>
        <Grid item md={8} sm={12} xs={12}>
          <div className={classes.localPreviewContainer}>
            <LocalVideoPreview identity={name} />
          </div>
          <div className={classes.mobileButtonBar}>
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              <ToggleAudioButton
                key={`mobile-audio-${componentKey}`}
                disabled={disableButtons}
                CustomToggleButton={StyledMobileButton}
              />
              <ToggleVideoButton
                key={`mobile-video-${componentKey}`}
                disabled={disableButtons}
                CustomToggleButton={StyledMobileButton}
              />
              <SettingsMenu key={`mobile-settings-${componentKey}`} CustomToggleButton={StyledMobileButton} />
            </Box>
          </div>
        </Grid>
        <Grid item md={1} sm={12} xs={12}>
          <Grid container direction="column" justifyContent="space-between" style={{ height: '100%' }}>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <ToggleAudioButton
                key={`desktop-audio-${componentKey}`}
                disabled={disableButtons}
                CustomToggleButton={StyledDeviceButton}
              />
              <ToggleVideoButton
                key={`desktop-video-${componentKey}`}
                disabled={disableButtons}
                CustomToggleButton={StyledDeviceButton}
              />
              <SettingsMenu key={`desktop-settings-${componentKey}`} CustomToggleButton={StyledDeviceButton} />
            </Box>
          </Grid>
        </Grid>
        <Grid item md={1} sm={12} xs={12}></Grid>
        <Grid item md={12} sm={12} xs={12}>
          <Grid container>
            <div className={classes.joinButtons}>
              {!user?.isExpert && (
                <NaosButton
                  id="video-calls-back"
                  variant="tertiary"
                  color="primary"
                  label={t('PREVIEW.DEVICES.BACK')}
                  onClick={() => setStep(Steps.roomStep)}
                ></NaosButton>
              )}
              <NaosButton
                id="video-calls-join"
                variant="primary"
                color="primary"
                label={t('PREVIEW.DEVICES.JOIN')}
                data-cy-join-now
                onClick={handleJoin}
                disabled={disableButtons}
              />
            </div>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
}
